import React, { useMemo } from 'react';
import ReportLayout from 'layouts/ReportLayout';

// Import report-specific components
import R0006KPISection from './components/R0006KPISection';
import R0006TablesSection from './components/R0006TablesSection';
import R0006LoadingOverlay from './components/R0006LoadingOverlay';
import AdjustmentFootnote from 'components/common/reports/AdjustmentFootnote';

// Import hooks and utilities
import useR0006Data from './hooks/useR0006Data';
import useR0006Header from './hooks/useR0006Header';
import { formatDateRangeSubtitle } from './utils/dateUtils';

// Import constants
import { R0006_CONFIG } from './constants';

/**
 * R0006 - Efficiency & Productivity Report
 * Common implementation that works across all clients
 */
export default function R0006() {
  // 🚨 Get loading states from useR0006Data
  const {
    dateRange,
    selectedArea,
    reportData,
    dataReady,
    pageLoading,
    loadingProgress,
    loadingMessage,
    pendingSelection,
    dataError,
    handleDateRangeSubmit,
    handleAreaChange,
    handleExport,
    // Area functionality
    selectedAreaData,
    areasData,
    handleTableRowClick,
    // Access control
    hasAccess,
    accessMessage,
    hasFullAccess,
    // Threshold data for adjustments
    thresholds,
    storeMap
  } = useR0006Data();


  // Prepare data for AdjustmentFootnote component
  const visibleStoreIds = useMemo(() => {
    if (!reportData?.restaurants) return [];
    return reportData.restaurants
      .map(r => r.storeId)
      .filter(Boolean); // Filter out any null/undefined storeIds
  }, [reportData]);

  const storeThresholds = useMemo(() => {
    if (!storeMap || storeMap.size === 0) return {};
    const thresholdsObj = {};
    for (const [storeName, storeData] of storeMap.entries()) {
      if (storeData.storeRecordId && storeData.thresholds) {
        thresholdsObj[storeData.storeRecordId] = storeData.thresholds;
      }
    }
    return thresholdsObj;
  }, [storeMap]);

  // Header controls and modals using custom hook
  const { leftControls, centerControls, rightControls, modals } = useR0006Header({
    dateRange,
    selectedArea,
    dataReady,
    pageLoading,
    onDateRangeSubmit: handleDateRangeSubmit,
    onAreaChange: handleAreaChange,
    onExport: handleExport,
    reportData,
    hasFullAccess,
    filteredAreas: areasData
  });

  // Check access before rendering
  if (!hasAccess) {
    return (
      <ReportLayout
        title={R0006_CONFIG.REPORT_TITLE}
        subtitle="Access Denied"
        headerProps={{
          minWidth: R0006_CONFIG.MIN_WIDTH,
          maxWidth: R0006_CONFIG.MAX_WIDTH,
          theme: R0006_CONFIG.HEADER_THEME
        }}
      >
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          color: '#666',
          fontSize: '16px'
        }}>
          <h3 style={{ color: '#d32f2f', marginBottom: '20px' }}>Access Restricted</h3>
          <p>{accessMessage}</p>
          <p style={{ marginTop: '20px', fontSize: '14px' }}>
            Please contact your administrator if you believe you should have access to this report.
          </p>
        </div>
      </ReportLayout>
    );
  }

  return (
    <>
      <ReportLayout
        title={R0006_CONFIG.REPORT_TITLE}
        subtitle={formatDateRangeSubtitle(
          dateRange,
          selectedArea === 'Area Summary' && !hasFullAccess ? 'Area' : selectedArea
        )}
        headerProps={{
          leftControls: leftControls,
          centerControls: centerControls,
          rightControls: rightControls,
          minWidth: R0006_CONFIG.MIN_WIDTH,
          maxWidth: R0006_CONFIG.MAX_WIDTH,
          theme: R0006_CONFIG.HEADER_THEME
        }}
      >
        
        {/* KPI Cards Section */}
        <R0006KPISection
          selectedArea={selectedArea}
          dateRange={dateRange}
          reportData={reportData}
          dataReady={dataReady}
        />

        {/* Tables Section */}
        <R0006TablesSection
          reportData={reportData}
          dataReady={dataReady}
          selectedArea={selectedArea}
          dateRange={dateRange}
          currentView={reportData?.viewType || 'areas'}
          isAreasView={(selectedArea === R0006_CONFIG.DEFAULT_AREA || selectedArea === 'Area') && reportData?.viewType === 'areas'}
          selectedAreaData={selectedAreaData}
          onTableRowClick={handleTableRowClick}
          dataError={dataError}
          loadingMessage={loadingMessage}
        />

        {/* Adjustment Footnote - shows when avgCheckHigh thresholds are configured */}
        {dataReady && (
          <AdjustmentFootnote
            storeIds={visibleStoreIds}
            thresholds={thresholds}
            storeThresholds={storeThresholds}
          />
        )}
      </ReportLayout>

      {/* Modals (DateRangeSelectionModal, etc.) */}
      {modals}

      {/* 🚨 Loading Overlay with Debug Props */}
      <R0006LoadingOverlay
        pageLoading={pageLoading}
        loadingProgress={loadingProgress}
        loadingMessage={loadingMessage}
        pendingSelection={pendingSelection}
        selectedArea={selectedArea}
      />
    </>
  );
}